import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import ProtectedRoutes from "components/protectedRoutes/ProtectedRoutes";
import SuperUserProtectedRoute from "components/protectedRoutes/SuperUserProtectedRoute";

import V2ProtectedRoute from "v2/components/V2ProtectedRoute";
import V2Layout from "v2/views/V2Layout";
import V2DefaultRedirect from "v2/views/V2DefaultRedirect";
import WorkspaceOverview from "v2/views/components/WorkspaceOverview";
import TeamOverview from "v2/views/components/TeamOverview";
import SessionOverview from "v2/views/components/SessionOverview";
import SuperUserDashboard from "v2/views/SuperUserDashboard";
import ManufacturingDashboard from "v2/views/ManufacturingDashboard";
import BatchDetail from "v2/views/BatchDetail";
import WearableDetail from "v2/views/WearableDetail";

// V2 Auth
import V2AuthLayout from "v2/views/layouts/V2AuthLayout";
import V2Login from "v2/views/auth/Login";
import V2Register from "v2/views/auth/Register";
import V2VerifyEmailSent from "v2/views/auth/VerifyEmailSent";
import V2VerifyEmail from "v2/views/auth/VerifyEmail";
import V2ForgotPassword from "v2/views/auth/ForgotPassword";
import V2ResetPassword from "v2/views/auth/ResetPassword";
import V2AcceptInvite from "v2/views/auth/AcceptInvite";
import JoinTeam from "v2/views/join/JoinTeam";
import ManageConsent from "v2/views/join/ManageConsent";
import JoinLayout from "v2/views/join/JoinLayout";
import V2NotFound from "v2/views/V2NotFound";
import V2AccountSettings from "v2/views/AccountSettings";
import V2WorkspaceSettings from "v2/views/WorkspaceSettings";

// Development only: the Case dashboard against a simulated case. The whole
// branch (and its chunk) is dropped from production builds.
const CaseDashboardDevPage =
  process.env.NODE_ENV === "development"
    ? React.lazy(() =>
        import("v2/views/components/CaseDashboard/dev/CaseDashboardDevPage")
      )
    : null;

const AppRoutes = () => {
  return (
    <Routes>
      {CaseDashboardDevPage && (
        <Route
          path="/dev/case-dashboard"
          element={
            <Suspense fallback={null}>
              <CaseDashboardDevPage />
            </Suspense>
          }
        />
      )}

      {/* V2 Auth Routes - Public */}
      <Route path="/v2/auth" element={<V2AuthLayout />}>
        <Route path="login" element={<V2Login />} />
        <Route path="register" element={<V2Register />} />
        <Route path="verify-email-sent" element={<V2VerifyEmailSent />} />
        <Route path="verify" element={<V2VerifyEmail />} />
        <Route path="forgot-password" element={<V2ForgotPassword />} />
        <Route path="reset-password" element={<V2ResetPassword />} />
        <Route path="*" element={<Navigate to="/v2/auth/login" replace />} />
      </Route>

      {/* V2 Invite Accept - Public, uses auth layout */}
      <Route path="/v2/invite" element={<V2AuthLayout />}>
        <Route path="accept" element={<V2AcceptInvite />} />
      </Route>

      {/* Team invite link - Public: parents and players joining a team */}
      <Route path="/v2/join" element={<JoinLayout />}>
        <Route path=":token" element={<JoinTeam />} />
      </Route>

      {/* A guardian's private link to change consent - Public */}
      <Route path="/v2/consent" element={<JoinLayout />}>
        <Route path=":token" element={<ManageConsent />} />
      </Route>

      {/* V2 Dashboard Routes */}
      <Route element={<ProtectedRoutes />}>
        {/* Redirect /v2 to default workspace */}
        <Route path="/v2" element={<V2DefaultRedirect />} />

        {/* Account settings (not workspace-scoped) */}
        <Route path="/v2/settings" element={<V2AccountSettings />} />

        {/* Workspace-scoped v2 routes */}
        <Route element={<V2ProtectedRoute />}>
          <Route path="/v2/:workspaceId" element={<V2Layout />}>
            <Route index element={<WorkspaceOverview />} />
            <Route path="team/:teamId" element={<TeamOverview />} />
            <Route
              path="team/:teamId/session/:sessionId"
              element={<SessionOverview />}
            />
            <Route path="settings" element={<V2WorkspaceSettings />} />
          </Route>
        </Route>

        {/* V2 404 catch-all */}
        <Route path="/v2/*" element={<V2NotFound />} />
      </Route>

      {/* Super User Admin Dashboard */}
      <Route element={<SuperUserProtectedRoute />}>
        <Route path="/v2/admin/default" element={<SuperUserDashboard />} />
        {/* Manufacturing admin (batches / wearables / audit trail) */}
        <Route
          path="/v2/admin/manufacturing"
          element={<ManufacturingDashboard />}
        />
        <Route
          path="/v2/admin/manufacturing/batches/:batchId"
          element={<BatchDetail />}
        />
        <Route
          path="/v2/admin/manufacturing/wearables/:bleMac"
          element={<WearableDetail />}
        />
      </Route>

      {/* Root redirect to V2 */}
      <Route path="/" element={<Navigate to="/v2" replace />} />

      {/* Fallback - redirect any unknown path to V2 */}
      <Route path="*" element={<Navigate to="/v2" replace />} />
    </Routes>
  );
};

export default AppRoutes;
